/**
 * Datasets 목록의 조회 축.
 *
 * 축 상태는 URL 이 소유한다 — 공유한 링크가 조건을 잃지 않게 한다(규칙 §13). 숨김 축만 서버
 * 쿼리로 나가고 **검색은 클라이언트 필터**다: BE 에 검색 파라미터가 없고 목록이 배열 하나라,
 * 글자를 칠 때마다 다시 받을 이유가 없다.
 *
 * Owns:
 * - 축 값, URL 코덱, 검색 매칭 규칙, 적용 조건 칩의 구성.
 *
 * Does not own:
 * - 칩의 해제 동작 배선 — 페이지. 표 페이지네이션 — `page/DatasetTableSource.ts`.
 */
import type { Dataset } from "../domain/dataset";

export type DatasetFilters = {
  search: string;
  includeHidden: boolean;
};

export type DatasetFilterAxis = "search" | "includeHidden";

export type DatasetFilterChip = {
  axis: DatasetFilterAxis;
  label: string;
};

export const EMPTY_DATASET_FILTERS: DatasetFilters = { search: "", includeHidden: false };

const SEARCH_PARAM = "q";
const INCLUDE_HIDDEN_PARAM = "include_hidden";

export function readDatasetFilters(params: URLSearchParams): DatasetFilters {
  return {
    search: (params.get(SEARCH_PARAM) ?? "").trim(),
    // 값이 있으면 켜진 것으로 본다 — "1" 만 받으면 손으로 고친 링크가 조용히 꺼진다.
    includeHidden: params.get(INCLUDE_HIDDEN_PARAM) === "1",
  };
}

/** 기본값인 축은 URL 에서 지운다 — 조건이 없는 화면의 주소가 길어지지 않게. */
export function writeDatasetFilters(
  params: URLSearchParams,
  filters: DatasetFilters,
): URLSearchParams {
  const next = new URLSearchParams(params);

  if (filters.search === "") {
    next.delete(SEARCH_PARAM);
  } else {
    next.set(SEARCH_PARAM, filters.search);
  }

  if (filters.includeHidden) {
    next.set(INCLUDE_HIDDEN_PARAM, "1");
  } else {
    next.delete(INCLUDE_HIDDEN_PARAM);
  }

  return next;
}

export function hasDatasetFilters(filters: DatasetFilters): boolean {
  return filters.search !== "" || filters.includeHidden;
}

export function clearDatasetFilterAxis(
  filters: DatasetFilters,
  axis: DatasetFilterAxis,
): DatasetFilters {
  return axis === "search" ? { ...filters, search: "" } : { ...filters, includeHidden: false };
}

export function datasetFilterChips(filters: DatasetFilters): readonly DatasetFilterChip[] {
  const chips: DatasetFilterChip[] = [];
  if (filters.search !== "") {
    chips.push({ axis: "search", label: `검색: ${filters.search}` });
  }
  if (filters.includeHidden) {
    chips.push({ axis: "includeHidden", label: "숨긴 Dataset 포함" });
  }
  return chips;
}

/** 이름과 원본 주소를 함께 본다 — 사용자가 기억하는 것이 둘 중 하나다. */
export function matchesDatasetSearch(dataset: Dataset, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") {
    return true;
  }

  return (
    dataset.displayName.toLowerCase().includes(needle) ||
    dataset.sourceRef.toLowerCase().includes(needle)
  );
}
